import { Contact } from '@/shared/types/api'
import { isEmailValid } from '@/shared/utils/validators'

/**
 * Phone-like string for participants-select and similar inputs:
 * optional leading +/*, digits and DTMF pause `w`
 * (e.g. 84951111111www2365 — dial then send DTMF after pauses).
 */
export const isPhoneNumber = (text: string): boolean => !!text.match(/^(\+|\*)?[0-9w]+$/i)

export const isEmail = (text: string): boolean => isEmailValid(text)

export type PhoneTokenParts = {
  base: string
  suffix: string
  normalizedBase: string
}

const normalizeBase = (base: string): string => base.startsWith('+') ? base.slice(1) : base

/**
 * Phone token grammar (used in phones-like inputs):
 * - digits (and DTMF pause `w`)
 * - base*digits*digits... (base may include `w`, e.g. 8495w111*4)
 *
 * Search uses only `base`, while `suffix` is preserved for display/backend.
 */
export const parsePhoneToken = (raw: string): PhoneTokenParts | null => {
  const value = raw.trim()
  if (!value) return null

  // Backend rejects '#', so we treat any token containing it as invalid.
  if (value.includes('#')) return null

  const segments = value.split('*')
  const base = segments[0]
  if (!base || !base.match(/^(\+)?[0-9w]+$/i)) return null

  const suffixSegments = segments.slice(1)
  if (suffixSegments.some((s) => !s || !s.match(/^[0-9w]+$/i))) return null

  const suffix = suffixSegments.length ? `*${suffixSegments.join('*')}` : ''

  return {
    base,
    suffix,
    normalizedBase: normalizeBase(base),
  }
}

export const isPhoneToken = (raw: string): boolean => !!parsePhoneToken(raw)

const contactToPhone = (contact: Contact): string | undefined =>
  contact.internalNumber?.trim() || contact.mobilePhone?.trim() || undefined

export const draftPhonesToAddToBackend = (draftNumbersToAdd: (Contact | string)[]): string => Array.from(new Set(draftNumbersToAdd
  .map((number: Contact | string) => (typeof number === 'object' ? contactToPhone(number) : number))
  .filter((phone): phone is string => Boolean(phone)))).join(';')

export const internalNumbersFromContactList = (contactList: (Contact | string) []): string[] => {
  return Array.from(new Set(contactList
    .map((number: Contact | string) => (typeof number === 'object' ? contactToPhone(number) : number))
    .filter((phone): phone is string => Boolean(phone))))
}

export const PARTICIPANTS_SELECT_SUGGESTIONS_ATTR = 'data-participants-select-suggestions'

export const getParticipantsSelectSuggestionsElements = (): HTMLElement[] =>
  Array.from(document.querySelectorAll(`[${PARTICIPANTS_SELECT_SUGGESTIONS_ATTR}]`))
